import {
	PartConfigurationSchema,
	PartConfigurationSchemaProperty,
	PartConfigurationSubConfigurationValue,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";
import {
	getNodeInputValues,
	PropertyInputValues,
	PropertyModelNodePath,
	removeChildVersions,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelInputTree.ts";

/** A configuration of another tree to import, with the part number of its model. */
export interface PropertyModelImportSource {
	path: PropertyModelNodePath;
	partNo: string | undefined;
}

/** A source configuration imported as a new child of [property]. */
export interface PropertyModelImportAssignment {
	property: PartConfigurationSchemaProperty;
	source: PropertyModelImportSource;
}

/** The part number of the model that a new child of [property] gets in [schema]. */
export function getNewChildPartNo(
	schema: PartConfigurationSchema,
	property: PartConfigurationSchemaProperty,
): string | undefined {
	return property.childModelKey == null ? undefined : schema.childModels[property.childModelKey]?.context.partNo;
}

/**
 * Assigns each source in order to the first of [targetProperties] whose new children are of the source's part, as the
 * component form matches imported sub-configurations by their part. A sub-configuration property takes one source.
 * Sources that no property takes are left out.
 */
export function assignImportTargetProperties(
	targetSchema: PartConfigurationSchema,
	targetProperties: PartConfigurationSchemaProperty[],
	sources: PropertyModelImportSource[],
): PropertyModelImportAssignment[] {
	const usedSingleNames = new Set<string>();
	return sources.flatMap((source) => {
		if (source.partNo == null) return [];
		const property = targetProperties.find(
			(candidate) =>
				getNewChildPartNo(targetSchema, candidate) === source.partNo &&
				(candidate.datatype === "SUB_CONFIGURATION_LIST" || !usedSingleNames.has(candidate.name)),
		);
		if (property == null) return [];
		if (property.datatype !== "SUB_CONFIGURATION_LIST") usedSingleNames.add(property.name);
		return [{ property, source }];
	});
}

/** Whether a property of [targetProperties] takes children of [partNo]. */
export function canImportPart(
	targetSchema: PartConfigurationSchema,
	targetProperties: PartConfigurationSchemaProperty[],
	partNo: string | undefined,
): boolean {
	return partNo != null && targetProperties.some((property) => getNewChildPartNo(targetSchema, property) === partNo);
}

/** The selected paths without those inside another selected path, which their ancestor imports already. */
export function getTopmostPaths(paths: PropertyModelNodePath[]): PropertyModelNodePath[] {
	return paths.filter(
		(path) =>
			!paths.some(
				(other) =>
					other.length < path.length &&
					other.every(
						(step, index) =>
							step.propertyName === path[index]?.propertyName && step.index === path[index]?.index,
					),
			),
	);
}

/**
 * The input values of the source configuration at [path] as a new child, with its sub-configurations, all without
 * their saved versions, so that they get the models of new children of the target.
 */
export function toImportedChild(
	sourceRootValues: PropertyInputValues,
	path: PropertyModelNodePath,
): PartConfigurationSubConfigurationValue | undefined {
	const values = getNodeInputValues(sourceRootValues, path);
	if (values == null) return undefined;
	return { valuesByPropertyName: removeChildVersions(structuredClone(values)) };
}

/** A new child to add to a property of a configuration. */
export interface PropertyModelNewChild {
	property: PartConfigurationSchemaProperty;
	child?: PartConfigurationSubConfigurationValue;
}

/** Imports sub-configurations from outside the form, such as from another line of the same offer. */
export interface PropertyModelSubConfigurationImportAction {
	title: string;
	onClick: (params: {
		/** The schema of the form, whose child models the imported children get. */
		targetSchema: PartConfigurationSchema;
		/** The properties of the target configuration to which children can be added. */
		targetProperties: PartConfigurationSchemaProperty[];
		targetLabel: string;
		importChildren: (children: PropertyModelNewChild[]) => Promise<void>;
	}) => Promise<void>;
}
